import React, { useState, useEffect } from 'react';
import { FileText, Download, CheckCircle, XCircle } from 'lucide-react';
import axios from 'axios';

const ExamAttendanceReport = () => {
  const [filters, setFilters] = useState({ 
    course_id: '', 
    semester_id: '', 
    exam_id: '',
    subject_id: ''
  });
  const [courses, setCourses] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [allSemesters, setAllSemesters] = useState([]);
  const [exams, setExams] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchCourses();
    fetchSemesters();
    fetchExams();
    fetchSubjects();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchSemesters = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/semesters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllSemesters(response.data);
      setSemesters(response.data);
    } catch (error) {
      console.error('Error fetching semesters:', error);
    }
  };

  const fetchExams = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/examination/exams`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExams(response.data);
    } catch (error) {
      console.error('Error fetching exams:', error);
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/subjects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubjects(response.data);
    } catch (error) {
      console.error('Error fetching subjects:', error);
    }
  };

  const handleCourseChange = (courseId) => {
    setFilters({ course_id: courseId, semester_id: '', exam_id: '', subject_id: '' });
    
    if (courseId) {
      const filteredSemesters = allSemesters.filter(sem => sem.course_id === courseId);
      setSemesters(filteredSemesters);
    } else {
      setSemesters(allSemesters);
    }
    setReportData(null);
  };

  const generateReport = async () => {
    if (!filters.exam_id || !filters.subject_id) {
      alert('Please select Exam and Subject');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/examination/attendance-report`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            exam_id: filters.exam_id,
            subject_id: filters.subject_id
          }
        }
      );

      setReportData(response.data);
      
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Failed to generate report: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const exportReport = () => {
    alert('Export functionality will be implemented');
  };

  const filteredExams = exams.filter(exam => 
    (!filters.course_id || exam.course_id === filters.course_id) &&
    (!filters.semester_id || exam.semester_id === filters.semester_id)
  );

  const filteredSubjects = subjects.filter(sub =>
    (!filters.course_id || sub.course_id === filters.course_id) &&
    (!filters.semester_id || sub.semester_id === filters.semester_id)
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Exam Attendance Report</h1>
          <p className="text-sm text-gray-500 mt-1">View attendance reports by exam and subject</p>
        </div>
        {reportData && (
          <button 
            onClick={exportReport}
            className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
          >
            <Download className="h-4 w-4" />
            Export Report
          </button>
        )}
      </div>

      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="text-lg font-semibold mb-4">Filters</h3>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Course *</label>
            <select 
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={filters.course_id} 
              onChange={(e) => handleCourseChange(e.target.value)}
            >
              <option value="">Select Course</option>
              {courses.map(course => (
                <option key={course.id} value={course.id}>{course.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Semester *</label>
            <select 
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={filters.semester_id} 
              onChange={(e) => setFilters({ ...filters, semester_id: e.target.value })}
              disabled={!filters.course_id}
            >
              <option value="">{filters.course_id ? 'Select Semester' : 'Select course first'}</option>
              {semesters.map(sem => (
                <option key={sem.id} value={sem.id}>{sem.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Exam *</label>
            <select 
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={filters.exam_id} 
              onChange={(e) => setFilters({ ...filters, exam_id: e.target.value })}
              disabled={!filters.course_id || !filters.semester_id}
            >
              <option value="">{filters.semester_id ? 'Select Exam' : 'Select semester first'}</option>
              {filteredExams.map(exam => (
                <option key={exam.id} value={exam.id}>{exam.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Subject *</label>
            <select 
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={filters.subject_id} 
              onChange={(e) => setFilters({ ...filters, subject_id: e.target.value })}
              disabled={!filters.course_id || !filters.semester_id}
            >
              <option value="">{filters.semester_id ? 'Select Subject' : 'Select semester first'}</option>
              {filteredSubjects.map(sub => (
                <option key={sub.id} value={sub.id}>{sub.name} ({sub.code})</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button 
              onClick={generateReport}
              disabled={!filters.exam_id || !filters.subject_id || loading}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
            >
              {loading ? 'Loading...' : 'Generate Report'}
            </button>
          </div>
        </div>
      </div>

      {reportData && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Students</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{reportData.total_students}</p>
              </div>
              <FileText className="h-12 w-12 text-blue-500" />
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Present</p>
                <p className="text-3xl font-bold text-green-600 mt-1">{reportData.present_count}</p>
              </div>
              <CheckCircle className="h-12 w-12 text-green-500" />
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Absent</p>
                <p className="text-3xl font-bold text-red-600 mt-1">{reportData.absent_count}</p>
              </div>
              <XCircle className="h-12 w-12 text-red-500" />
            </div>
          </div>
        </div>
      )}

      {reportData ? (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-4 bg-blue-50 border-b">
            <h3 className="font-semibold text-blue-900">{reportData.exam_name} - {reportData.subject_name}</h3>
            <p className="text-sm text-blue-700">Attendance Report | Marked on: {reportData.marked_at}</p>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">S.No</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Admission No</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student Name</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Attendance</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {reportData.attendance.map((record, index) => (
                <tr key={index} className={record.status === 'Absent' ? 'bg-red-50' : ''}>
                  <td className="px-4 py-3 text-sm text-gray-900">{index + 1}</td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    {record.admission_number || 'N/A'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">{record.student_name}</td>
                  <td className="px-4 py-3 text-center">
                    {record.status === 'Present' ? (
                      <span className="inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Present
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                        <XCircle className="h-3 w-3 mr-1" />
                        Absent
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">Select Exam and Subject to generate attendance report</p>
        </div>
      )}
    </div>
  );
};

export default ExamAttendanceReport;
